/**
 * useDailyUsage — real-time listener on /clients/{id}/dailyCounts/{YYYY-MM-DD}
 *
 * Mirrors useMonthlyUsage but for the dispatcher's daily counter. The key is
 * derived in America/Chicago so the visible "today" matches the dispatcher's
 * server-side daily cap (which now also keys in CST). On CST midnight the
 * hook re-derives the key and re-subscribes, so the count visibly resets
 * without requiring a page refresh.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const DEFAULT_DAILY_CAP = 10000;

interface DailyUsage {
  sent: number;
  limit: number;
  remaining: number;
  percent: number;
  dayKey: string;
  loading: boolean;
}

function currentDayKey(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year')?.value ?? '1970';
  const m = parts.find(p => p.type === 'month')?.value ?? '01';
  const d = parts.find(p => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

export function useDailyUsage(clientId: string | null): DailyUsage {
  const [dayKey, setDayKey] = useState<string>(currentDayKey());
  const [usage, setUsage] = useState<DailyUsage>({
    sent: 0,
    limit: DEFAULT_DAILY_CAP,
    remaining: DEFAULT_DAILY_CAP,
    percent: 0,
    dayKey,
    loading: true,
  });

  // Roll the dayKey at CST midnight. Re-checks every 60s — cheap, and any
  // skew between the wall clock and the CST boundary self-corrects within
  // a minute of midnight.
  useEffect(() => {
    const id = setInterval(() => {
      const next = currentDayKey();
      if (next !== dayKey) setDayKey(next);
    }, 60_000);
    return () => clearInterval(id);
  }, [dayKey]);

  useEffect(() => {
    if (!clientId) return;

    let currentSent = 0;
    let currentLimit = DEFAULT_DAILY_CAP;

    const push = () => {
      const remaining = Math.max(0, currentLimit - currentSent);
      const percent = currentLimit > 0 ? Math.round((currentSent / currentLimit) * 100) : 0;
      setUsage({ sent: currentSent, limit: currentLimit, remaining, percent, dayKey, loading: false });
    };

    const clientUnsub = onSnapshot(doc(db, 'clients', clientId), snap => {
      if (snap.exists()) {
        const s = (snap.data().settings as Record<string, unknown>) || {};
        currentLimit = typeof s.maxCallsPerDay === 'number' ? (s.maxCallsPerDay as number) : DEFAULT_DAILY_CAP;
        push();
      }
    });

    const dayUnsub = onSnapshot(
      doc(db, 'clients', clientId, 'dailyCounts', dayKey),
      snap => {
        currentSent = snap.exists() ? ((snap.data()?.sent as number) || 0) : 0;
        push();
      }
    );

    return () => {
      clientUnsub();
      dayUnsub();
    };
  }, [clientId, dayKey]);

  return usage;
}
